import './i18n/index.js';
import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { splitLanguagePath } from './i18n/locales';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './App.css';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DateTimeProvider } from './contexts/DateTimeContext';
import { UndoProvider } from './contexts/UndoContext';
import { TaskProvider } from './contexts/TaskContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import UpgradePrompt from './components/UpgradePrompt';
import ErrorBoundary from './components/ErrorBoundary';

// Landing Page Components
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import { WorkApp, BotHandoffs, BotSteps, BotCapabilities, BotApps, Devices, PlansTeaser, CompareStrip } from './components/BotSections';
import Security from './components/Security';
import FAQ from './components/FAQ';
import CTA from './components/CTA';
import Footer from './components/Footer';
import SEO from './components/SEO';
import { faqs } from './data/mock';

// Public Pages
import PricingPage from './pages/PricingPage';
import FeaturePage from './pages/FeaturePage';
import TeamPage from './pages/TeamPage';
import IntegrationsPage from './pages/IntegrationsPage';
import McpIntegrationPage from './pages/McpIntegrationPage';
import OAuthAuthorizePage from './pages/OAuthAuthorizePage';
import ResourcePage from './pages/ResourcePage';
import AboutPage from './pages/AboutPage';
import CareersPage from './pages/CareersPage';
import ContactPage from './pages/ContactPage';
import EnterprisePage from './pages/EnterprisePage';
import PressPage from './pages/PressPage';
import LegalPage from './pages/LegalPage';
import DevelopersPage from './pages/DevelopersPage';
import AlternativesPage from './pages/AlternativesPage';
import GuidePage from './pages/GuidePage';
import { HIDDEN_APPS } from './data/guides';
import InvitePage from './pages/InvitePage';
import NotFoundPage from './pages/NotFoundPage';
import NativeBridge from './native/NativeBridge';
import AiConsentDialog from './components/AiConsentDialog';
import PushPromptDialog from './components/PushPromptDialog';
import { isNative } from './native/platform';
import SharedProjectPage from './pages/SharedProjectPage';
import PublicTeamPage from './pages/PublicTeamPage';
import DocumentsPage from './pages/app/DocumentsPage';

// Auth Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import MagicLinkReturnPage from './pages/MagicLinkReturnPage';
import OAuthReturnPage from './pages/OAuthReturnPage';

// App Layout & Pages
//
// Eagerly imported: the small handful of pages a typical session opens first
// (Agenda / Tasks / Inbox / Projects) — splitting these out gains very little
// and would add a flash of skeleton on the most-trafficked routes.
//
// Lazy: the heavy or rarely-visited screens. PlannerPage pulls in the entire
// FullCalendar bundle (+plugins) which is the single biggest contributor to
// the main chunk; DocumentPage drags Tiptap + Excalidraw + Mermaid; the rest
// are either large or visited infrequently. Code-splitting them keeps the
// initial app bundle lean and means a heavy page's cost is paid only when
// the user actually opens it.
import AppLayout from './layouts/AppLayout';
import AgendaPage from './pages/app/AgendaPage';
import TasksPage from './pages/app/TasksPage';
import ProjectsPage from './pages/app/ProjectsPage';
import HabitsPage from './pages/app/HabitsPage';
import SpotlightPage from './pages/app/SpotlightPage';
import NotificationsPage from './pages/app/NotificationsPage';
import ChatLinkPage from './pages/app/ChatLinkPage';
import RouteProgressBar from './components/RouteProgressBar';
import { RouteChunkFallback } from './components/skeletons/PageSkeletons';
import { GOOGLE_APP_INTEGRATIONS } from './lib/constants';
import { signInPathFor } from './lib/signInReturn';

import { lazyPage } from './lib/lazyPage';
// The bottom tabs' screens live with the phone's tab stack (layouts/TabScreens.jsx).
import { PlannerPage, BotsPage, ChatsPage, SchedulesPage } from './layouts/TabScreens';

const FocusTimePage = lazyPage(() => import('./pages/app/FocusTimePage'));
const BufferTimePage = lazyPage(() => import('./pages/app/BufferTimePage'));
const AnalyticsPage = lazyPage(() => import('./pages/app/AnalyticsPage'));
const SettingsPage = lazyPage(() => import('./pages/app/SettingsPage'));
const BillingPage = lazyPage(() => import('./pages/app/BillingPage'));
const SubscriptionPage = lazyPage(() => import('./pages/app/SubscriptionPage'));
const WebhooksPage = lazyPage(() => import('./pages/app/WebhooksPage'));
const GmailPage = lazyPage(() => import('./pages/app/GmailPage'));
const TrashPage = lazyPage(() => import('./pages/app/TrashPage'));
const DocumentPage = lazyPage(() => import('./pages/app/DocumentPage'));
const HelpPage = lazyPage(() => import('./pages/app/HelpPage'));
const AdminPage = lazyPage(() => import('./pages/app/AdminPage'));
const ConnectionCallbackPage = lazyPage(() => import('./pages/app/ConnectionCallbackPage'));
// The desktop app's own surfaces (desktop/, Electron): the hive, the honeycomb and quick
// panel, the bot's computer, and the browser page that signs the app in.
const HivePage = lazyPage(() => import('./desktop/HivePage'));
const QuickPage = lazyPage(() => import('./desktop/QuickPage'));
const BotViewPage = lazyPage(() => import('./desktop/BotViewPage'));
const ConnectPage = lazyPage(() => import('./desktop/ConnectPage'));

// The phone app has every screen on the device: they load right after it starts, so
// opening one never shows a loading state.
if (isNative()) {
  setTimeout(() => [PlannerPage, FocusTimePage, BufferTimePage, AnalyticsPage, SettingsPage, BillingPage, SubscriptionPage, WebhooksPage, GmailPage, TrashPage, DocumentPage, HelpPage, ChatsPage, BotsPage, SchedulesPage, ConnectionCallbackPage].forEach((page) => page.preload().catch(() => {})), 1200);
}
const onDesktopSurface = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/desktop/');

const HomePage = () => {
  const { t } = useTranslation('landing');
  const faqItems = faqs.map(({ key }) => ({ question: t(`faq.items.${key}.q`), answer: t(`faq.items.${key}.a`) }));
  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={t('home.seo_title')}
        description={t('home.seo_description')}
        canonical="/"
        image="/screenshots/work-app/agenda.png"
        faqItems={faqItems}
      />
      <Navbar />
      <Hero />
      <BotSteps />
      <BotHandoffs />
      <BotCapabilities />
      <WorkApp />
      <BotApps />
      <Devices />
      <PlansTeaser />
      <CompareStrip />
      <Security />
      <FAQ />
      <CTA />
      <Footer />
    </div>
  );
};

// Google OAuth Callback Redirect Component
const GoogleOAuthCallback = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('code');
  const state = searchParams.get('state');

  // Redirect to settings page with the OAuth code
  const redirectUrl = `/app/settings?tab=integrations${code ? `&code=${encodeURIComponent(code)}` : ''}${state ? `&state=${encodeURIComponent(state)}` : ''}`;

  return <Navigate to={redirectUrl} replace />;
};

// Gmail OAuth Callback Redirect Component
const GmailOAuthCallback = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('code');

  const redirectUrl = `/app/gmail${code ? `?code=${encodeURIComponent(code)}` : ''}`;

  return <Navigate to={redirectUrl} replace />;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  // After signing in the person comes back to the page they opened (lib/signInReturn.js).
  if (!isAuthenticated) {
    return <Navigate to={signInPathFor(location)} replace />;
  }

  return children;
};

// Admin-only Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/app" replace />;
  }

  return children;
};

function GlobalUpgradeModal() {
  const [upgradeInfo, setUpgradeInfo] = useState(null);

  const handleUpgrade = useCallback((e) => {
    const detail = e.detail;
    const feature = detail?.feature || detail?.resource?.replace(/_/g, ' ') || '';
    const message = detail?.message || '';
    const requiredPlan = detail?.feature ? 'pro' : 'basic';
    setUpgradeInfo({ feature, message, requiredPlan });
  }, []);

  useEffect(() => {
    window.addEventListener('upgradNeeded', handleUpgrade);
    return () => window.removeEventListener('upgradNeeded', handleUpgrade);
  }, [handleUpgrade]);

  useEffect(() => {
    if (!upgradeInfo) return;
    const handleKey = (e) => { if (e.key === 'Escape') setUpgradeInfo(null); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [upgradeInfo]);

  if (!upgradeInfo) return null;
  return (
    <UpgradePrompt
      feature={upgradeInfo.feature}
      requiredPlan={upgradeInfo.requiredPlan}
      message={upgradeInfo.message}
      inModal
      onClose={() => setUpgradeInfo(null)}
    />
  );
}

// Scroll to top on every route change. The SPA keeps window scroll position
// across client-side navigations (BG_108: navigating from the mobile menu kept
// the previous page's scroll offset). Resetting on pathname change guarantees
// each page starts at the top. A link with a #hash (such as /#work-app from the
// guide pages) goes to that section instead, once it has rendered.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash && !pathname.startsWith('/public/team')) {
      let tries = 0;
      const timer = setInterval(() => {
        const el = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (el || ++tries > 20) {
          clearInterval(timer);
          if (el) el.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      }, 50);
      return () => clearInterval(timer);
    }
    // Use behavior:'instant' to override the global `scroll-behavior: smooth`
    // CSS (BG_44): otherwise the destination page first renders at the previous
    // scroll offset and only animates to the top afterwards.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    return undefined;
  }, [pathname, hash]);
  return null;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      {/* The apps open on the app itself; the public site stays on the web. */}
      <Route path="/" element={isNative() ? <Navigate to="/app" replace /> : <HomePage />} />
      {/* In the apps plans are bought in the store (App Store 3.1.1): the app's own page. */}
      <Route path="/pricing" element={isNative() ? <Navigate to="/app/subscription" replace /> : <PricingPage />} />
      {/* The student, nonprofit and startup discount pages offered discounts that do not exist */}
      <Route path="/pricing/:programId" element={<Navigate to={isNative() ? '/app/subscription' : '/pricing'} replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/magic" element={<MagicLinkReturnPage />} />
      <Route path="/auth/oauth/return" element={<OAuthReturnPage />} />
      <Route path="/auth/oauth/link-return" element={<OAuthReturnPage linkMode />} />
      <Route path="/desktop/hive" element={<Suspense fallback={null}><HivePage /></Suspense>} />
      <Route path="/desktop/quick" element={<Suspense fallback={null}><QuickPage /></Suspense>} />
      <Route path="/desktop/botview" element={<Suspense fallback={null}><ProtectedRoute><BotViewPage /></ProtectedRoute></Suspense>} />
      <Route path="/desktop/connect" element={<Suspense fallback={null}><ConnectPage /></Suspense>} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/share/:shareToken" element={<SharedProjectPage />} />
      <Route path="/public/team/:shareToken" element={<PublicTeamPage />} />

      {/* Google OAuth Callback - redirects to settings */}
      <Route path="/api/auth/google/callback" element={<GoogleOAuthCallback />} />
      {/* Gmail OAuth Callback - redirects to Gmail page */}
      <Route path="/api/auth/gmail/callback" element={<GmailOAuthCallback />} />

      {/* Removed features - redirect to closest live route */}
      <Route path="/features/meetings" element={<Navigate to="/features/calendar" replace />} />
      <Route path="/features/scheduling-links" element={<Navigate to="/features/calendar" replace />} />
      {/* The Inbox page went into Tasks; the phone app has its own page with the desktop app */}
      <Route path="/features/inbox" element={<Navigate to="/features/task-management" replace />} />
      <Route path="/features/mobile-app" element={<Navigate to="/download/mobile" replace />} />
      {/* Gmail and Google Calendar in the app are hidden (GOOGLE_APP_INTEGRATIONS) */}
      {!GOOGLE_APP_INTEGRATIONS && <Route path="/features/gmail-integration" element={<Navigate to="/integrations" replace />} />}
      {!GOOGLE_APP_INTEGRATIONS && <Route path="/integrations/gmail" element={<Navigate to="/integrations" replace />} />}
      {!GOOGLE_APP_INTEGRATIONS && <Route path="/integrations/google-calendar" element={<Navigate to="/integrations" replace />} />}

      {/* Feature Pages */}
      {/* The old AI assistant's feature page: the bots took its place. */}
      <Route path="/features/ai-assistant" element={<Navigate to="/ai-bots" replace />} />
      <Route path="/features/:featureId" element={<FeaturePage />} />
      
      {/* Team Pages */}
      <Route path="/teams/:teamId" element={<TeamPage />} />
      
      {/* Integration Pages */}
      <Route path="/integrations" element={<IntegrationsPage />} />
      {/* MCP server config - must precede the dynamic :integrationId route */}
      <Route path="/integrations/mcp" element={<McpIntegrationPage />} />
      {/* OAuth 2.1 consent screen for MCP clients (Claude Desktop, etc.) */}
      <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
      {/* Apps bots cannot use yet (data/guides.js HIDDEN_APPS) */}
      {HIDDEN_APPS.map((id) => <Route key={id} path={`/integrations/${id}`} element={<Navigate to="/ai-bots/connected-apps" replace />} />)}
      <Route path="/integrations/:integrationId" element={<GuidePage fallback={<IntegrationsPage />} />} />
      
      {/* Alternatives / competitor comparison pages */}
      <Route path="/alternatives" element={<AlternativesPage />} />
      <Route path="/alternatives/:competitor" element={<GuidePage fallback={<AlternativesPage />} />} />

      {/* Bots, freelancer use cases and glossary terms (content in locales/<lang>/<ns>.json) */}
      <Route path="/ai-bots" element={<GuidePage />} />
      <Route path="/ai-bots/:slug" element={<GuidePage />} />
      <Route path="/use-cases" element={<GuidePage />} />
      <Route path="/use-cases/:slug" element={<GuidePage />} />
      <Route path="/glossary/:slug" element={<GuidePage />} />
      <Route path="/download" element={<GuidePage />} />
      <Route path="/download/:slug" element={<GuidePage />} />

      {/* Resource Pages */}
      <Route path="/blog" element={<ResourcePage />} />
      <Route path="/help" element={<ResourcePage />} />
      <Route path="/glossary" element={<ResourcePage />} />
      <Route path="/tools/focus-timer" element={<ResourcePage />} />
      
      {/* Company Pages */}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/enterprise" element={<EnterprisePage />} />
      <Route path="/press" element={<PressPage />} />
      <Route path="/developers" element={<DevelopersPage />} />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/security" element={<LegalPage />} />
      <Route path="/cookies" element={<LegalPage />} />
      <Route path="/ai-policy" element={<LegalPage />} />
      <Route path="/subprocessors" element={<LegalPage />} />
      <Route path="/delete-account" element={<LegalPage />} />
      
      {/* Protected App Routes */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AgendaPage />} />
        <Route path="agenda" element={<AgendaPage />} />
        <Route path="planner" element={<PlannerPage />} />
        {/* Removed feature - redirect to planner */}
        <Route path="meetings" element={<Navigate to="/app/planner" replace />} />
        {/* The Inbox page was merged into My Tasks (its count) and the Planner (scheduling) */}
        <Route path="inbox" element={<Navigate to="/app/planner" replace />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="teams" element={<SettingsPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="focus-time" element={<FocusTimePage />} />
        <Route path="habits" element={<HabitsPage />} />
        <Route path="spotlight" element={<SpotlightPage />} />

        <Route path="buffer-time" element={<BufferTimePage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="settings" element={<SettingsPage />} />
        {/* The old AI assistant: its chat is the bots now. */}
        <Route path="assistant" element={<Navigate to="/app/bots" replace />} />
        <Route path="bots" element={<BotsPage />} />
        <Route path="bots/scheduled" element={<SchedulesPage />} />
        {/* Chats: the one history and a chat in it (/app/chats/<id>, /app/chats/new). Older links
            to a bot's page or its chats land there (ChatLinkPage). */}
        <Route path="chats/:chatId?" element={<ChatsPage />} />
        <Route path="bots/:botId" element={<ChatLinkPage />} />
        <Route path="bots/:botId/chats" element={<ChatLinkPage />} />
        <Route path="connections/callback" element={<ConnectionCallbackPage />} />
        <Route path="webhooks" element={<WebhooksPage />} />
        <Route path="gmail" element={GOOGLE_APP_INTEGRATIONS ? <GmailPage /> : <Navigate to="/app" replace />} />
        <Route path="trash" element={<TrashPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="documents/:documentId" element={<DocumentPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  const [mobileOffset, setMobileOffset] = useState('80px');
  useEffect(() => {
    const update = () => {
      // On mobile (below md=768), push toasts above the 64px bottom nav + safe area
      const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
      setMobileOffset(isMobile ? 'calc(64px + env(safe-area-inset-bottom) + 16px)' : '24px');
    };
    update();
    const mql = window.matchMedia('(max-width: 767px)');
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);
  return <Toaster position="bottom-right" richColors theme={theme} closeButton offset={mobileOffset} />;
}

// See-through desktop windows wait without a dark screen.
const LoadingSpinner = () => (onDesktopSurface() ? null : (
  <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
));

// If the URL begins with /<supported-lang>/, set BrowserRouter basename to that prefix
// so all existing <Link to="/x"> calls keep working without any refactor. Switching
// languages from the dropdown re-navigates with a full reload (see LanguageContext).
const routerBasename = (() => {
  if (typeof window === 'undefined') return '/';
  const { lang } = splitLanguagePath(window.location.pathname);
  return lang ? `/${lang}` : '/';
})();

function App() {
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <BrowserRouter basename={routerBasename}>
            <ThemeProvider>
              <LanguageProvider>
                <AuthProvider>
                  <DateTimeProvider>
                    <UndoProvider>
                      <TaskProvider>
                        <ErrorBoundary>
                          <Suspense fallback={<LoadingSpinner />}>
                            <ScrollToTop />
                            <AppRoutes />
                            <GlobalUpgradeModal />
                            <NativeBridge />
                            <AiConsentDialog />
                            <PushPromptDialog />
                            <ThemedToaster />
                          </Suspense>
                        </ErrorBoundary>
                      </TaskProvider>
                    </UndoProvider>
                  </DateTimeProvider>
                </AuthProvider>
              </LanguageProvider>
            </ThemeProvider>
          </BrowserRouter>
        </HelmetProvider>
        {process.env.NODE_ENV === 'development' &&
          typeof window !== 'undefined' &&
          window.localStorage?.getItem('hideDevtools') !== '1' && !onDesktopSurface() && (
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
        )}
      </QueryClientProvider>
    </div>
  );
}

export default App;
